import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { removeFieldProvenance, type FieldProvenanceRow } from '../../lib/enrichment/api';

interface Props {
  provenance: FieldProvenanceRow[];
  onReload: () => Promise<void>;
}

export function ProvenanceList({ provenance, onReload }: Props) {
  const [removing, setRemoving] = useState<string | null>(null);

  const onRemove = async (id: string) => {
    const reason = window.prompt('Motivazione rimozione (es. "richiesta diritto all\'oblio del lead")?');
    if (reason === null) return;
    setRemoving(id);
    try {
      await removeFieldProvenance(id, reason || undefined);
      await onReload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      window.alert(`Errore rimozione: ${msg}`);
    } finally {
      setRemoving(null);
    }
  };

  // Raggruppa per field_name (un campo può avere più source nel tempo)
  const grouped = new Map<string, FieldProvenanceRow[]>();
  for (const p of provenance) {
    const arr = grouped.get(p.field_name) ?? [];
    arr.push(p);
    grouped.set(p.field_name, arr);
  }

  return (
    <div className="border border-[rgba(55,53,47,0.09)] rounded-md divide-y divide-[rgba(55,53,47,0.09)]">
      {Array.from(grouped.entries()).map(([field, entries]) => {
        const latest = entries[0]!;
        return (
          <div key={field} className="flex items-start justify-between gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-[#37352f]">{prettyFieldName(field)}</div>
              <div className="text-[11px] text-[#9b9a97] mt-0.5 flex flex-wrap gap-x-1.5">
                <span>{prettySource(latest.source)}</span>
                <span>·</span>
                <span>{prettyBaseLegal(latest.base_legal)}</span>
                <span>·</span>
                <span>
                  {new Date(latest.captured_at).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
                {entries.length > 1 && (
                  <>
                    <span>·</span>
                    <span>{entries.length} versioni</span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => void onRemove(latest.id)}
              disabled={removing === latest.id}
              className="p-1 text-[#787774] hover:text-[#e03e3e] hover:bg-[rgba(224,62,62,0.06)] rounded transition-colors disabled:opacity-50"
              title="Rimuovi (diritto all'oblio)"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function prettyFieldName(name: string): string {
  const map: Record<string, string> = {
    business_name: 'Ragione sociale',
    contact_name: 'Nome contatto',
    email: 'Email',
    phone: 'Telefono',
    mobile_phone: 'Telefono mobile',
    vat_number: 'P.IVA',
    fiscal_code: 'Codice fiscale',
    pec_email: 'PEC',
    rea_code: 'REA',
    industry_code: 'Codice ATECO',
    industry_label: 'Settore (ATECO)',
    legal_address: 'Sede legale',
    city: 'Città',
    province: 'Provincia',
    postal_code: 'CAP',
    employees_count: 'Numero dipendenti',
    revenue_last_fy: 'Fatturato ultimo esercizio',
    founded_at: 'Data costituzione',
    company_status: 'Stato attività',
    website: 'Sito web',
    linkedin_url: 'LinkedIn',
    job_title: 'Ruolo',
    employer_name: 'Datore di lavoro',
    email_domain: 'Dominio email',
    email_has_mx_verified: 'MX email verificato',
  };
  return map[name] ?? name;
}

function prettyBaseLegal(bl: string): string {
  const map: Record<string, string> = {
    consenso: 'consenso esplicito',
    legittimo_interesse: 'legittimo interesse',
    registro_pubblico: 'registro pubblico',
    dichiarato_manualmente: 'inserito manualmente',
  };
  return map[bl] ?? bl;
}

function prettySource(src: string): string {
  const map: Record<string, string> = {
    form: 'form',
    meta: 'Meta Lead Ads',
    external_api: 'API esterna',
    whatsapp: 'WhatsApp',
    csv_import: 'import CSV',
    manual: 'manuale',
    internal_parser: 'parser interno',
    whois: 'WHOIS',
    vies: 'VIES',
    openapi_it: 'openapi.it',
    dropcontact: 'Dropcontact',
    fullenrich: 'FullEnrich',
    hunter: 'Hunter.io',
    apollo: 'Apollo.io',
  };
  return map[src] ?? src;
}
